import type {
  ShearAnchorPosition,
  ShearConnectionCategory,
  ShearConnectionCreateDTO,
  ShearConnectionDTO,
} from '@api/fastAPI.schemas'

/** Wandfuß before Wandkopf, the order every list and table uses. */
export const SHEAR_ANCHOR_POSITIONS: ShearAnchorPosition[] = ['bottom', 'top']

export const SHEAR_ANCHOR_POSITION_LABEL: Record<ShearAnchorPosition, string> = {
  bottom: 'Wandfuß',
  top: 'Wandkopf',
}

/**
 * Storey/placement/position category a shear anchor is configured for. A wall's
 * storey decides the storey part (storey 0 is the Erdgeschoss, the topmost
 * storey the Dachgeschoss, everything between an Obergeschoss), its placement
 * whether the inner- or outer-wall list applies, and the anchor position
 * whether it is the one at the wall's foot or at its head.
 */
// Listed top-down (Dach first, EG last), like the storey selects elsewhere.
const SHEAR_CONNECTION_STOREY_PLACEMENTS: { id: string; label: string }[] = [
  { id: 'roof_floor_inner', label: 'Dachgeschoss Innenwände' },
  { id: 'roof_floor_outer', label: 'Dachgeschoss Außenwände' },
  { id: 'upper_floor_inner', label: 'Obergeschoss Innenwände' },
  { id: 'upper_floor_outer', label: 'Obergeschoss Außenwände' },
  { id: 'ground_floor_inner', label: 'Erdgeschoss Innenwände' },
  { id: 'ground_floor_outer', label: 'Erdgeschoss Außenwände' },
]

export const SHEAR_CONNECTION_CATEGORIES: {
  id: ShearConnectionCategory
  label: string
  position: ShearAnchorPosition
}[] = SHEAR_CONNECTION_STOREY_PLACEMENTS.flatMap(({ id, label }) =>
  SHEAR_ANCHOR_POSITIONS.map(position => ({
    id: `${id}_${position}` as ShearConnectionCategory,
    label: `${label} ${SHEAR_ANCHOR_POSITION_LABEL[position]}`,
    position,
  })),
)

export const SHEAR_CONNECTION_CATEGORY_LABEL = Object.fromEntries(
  SHEAR_CONNECTION_CATEGORIES.map(({ id, label }) => [id, label]),
) as Record<ShearConnectionCategory, string>

/** The label without the position half, for places that state the position separately. */
export const SHEAR_CONNECTION_CATEGORY_PLACEMENT_LABEL = Object.fromEntries(
  SHEAR_CONNECTION_STOREY_PLACEMENTS.flatMap(({ id, label }) =>
    SHEAR_ANCHOR_POSITIONS.map(position => [`${id}_${position}`, label]),
  ),
) as Record<ShearConnectionCategory, string>

/** Resistances are stored in N and shown in kN. */
export const shearResistanceFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatShearResistance = (valueInNewton: number): string =>
  shearResistanceFormatter.format(valueInNewton / 1000)

/** The installation spacing, stored and shown in m. */
export const formatShearDistance = (valueInMeter: number): string =>
  shearResistanceFormatter.format(valueInMeter)

/**
 * The design shear flow the row of connectors carries, in N/m: one connector's
 * resistance over its spacing. That is what the check compares against.
 */
export const shearFlowResistance = (resistanceInNewton: number, distanceInMeter: number): number =>
  distanceInMeter > 0 ? resistanceInNewton / distanceInMeter : 0

/** The create/update payload for a connection: the comment must be a string. */
export const toShearConnectionCreate = (
  connection: ShearConnectionDTO,
): ShearConnectionCreateDTO => ({
  ...connection,
  export_comment: connection.export_comment ?? '',
})
